import type { LayerKind } from '../types';

/**
 * `color` overrides each glyph's built-in default — the consumer derives it from the layer's
 * tile-config paint (`line-color` / `circle-color` / `fill-color`). Area/circle glyphs
 * additionally take `outlineColor` (`fill-outline-color` / `circle-stroke-color`) for
 * their stroke, falling back to `color`. Both apply verbatim — a fully transparent
 * paint draws nothing, exactly like the map.
 */
type GlyphProps = { className?: string; color?: string; outlineColor?: string };

function LineGlyph({ className, color }: GlyphProps) {
  return (
    <svg
      role="img"
      aria-label="Solid line"
      width="16"
      height="12"
      viewBox="0 0 26 19"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      className={className}
    >
      <path
        d="M1 5.20265C6.36389 -1.58082 13.8432 -0.297661 12.7958 10.1579C11.7911 20.1869 19.716 19.7049 24.2 12.4496"
        stroke={color ?? '#E23B03'}
        strokeWidth="2"
        strokeLinecap="round"
      />
    </svg>
  );
}

function LineDashedGlyph({ className, color }: GlyphProps) {
  return (
    <svg
      role="img"
      aria-label="Dashed line"
      width="16"
      height="12"
      viewBox="0 0 26 19"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      className={className}
    >
      <path
        d="M1 5.19923C6.36389 -1.58423 13.8432 -0.301079 12.7958 10.1545C11.7911 20.1834 19.716 19.7015 24.2 12.4462"
        stroke={color ?? '#36A209'}
        strokeWidth="2"
        strokeLinecap="round"
        strokeDasharray="6 6"
      />
    </svg>
  );
}

function AreaGlyph({ className, color, outlineColor }: GlyphProps) {
  return (
    <svg
      role="img"
      aria-label="Outlined area"
      width="14"
      height="15"
      viewBox="0 0 21 22"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      className={className}
    >
      <path
        d="M9.10043 1C4.30043 0.999977 -1.67839 9.4 2.30031 11.8C6.27902 14.2 4.12168 18.8 9.72166 20.2C19.1217 21.6 22.3004 4.6 18.1004 6.8C13.9004 9 13.9004 1.00002 9.10043 1Z"
        stroke={outlineColor ?? color ?? '#F385AD'}
        strokeWidth="2"
      />
    </svg>
  );
}

function AreaFilledGlyph({ className, color, outlineColor }: GlyphProps) {
  return (
    <svg
      role="img"
      aria-label="Filled area"
      width="14"
      height="15"
      viewBox="0 0 21 22"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      className={className}
    >
      <path
        d="M9.10043 1C4.30043 0.999977 -1.67839 9.4 2.30031 11.8C6.27902 14.2 4.12168 18.8 9.72166 20.2C19.1217 21.6 22.3004 4.6 18.1004 6.8C13.9004 9 13.9004 1.00002 9.10043 1Z"
        fill={color ?? '#98BAE5'}
        fillOpacity="0.38"
        stroke={outlineColor ?? color ?? '#196FE0'}
        strokeWidth="2"
      />
    </svg>
  );
}

function CircleGlyph({ className, color, outlineColor }: GlyphProps) {
  return (
    <svg
      role="img"
      aria-label="Circle"
      width="14"
      height="14"
      viewBox="0 0 16 16"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      className={className}
    >
      {/* Fill applies only when the layer carries a fill color — same 38% as the area glyph. */}
      <circle
        cx="8"
        cy="8"
        r="6.5"
        fill={color ?? 'none'}
        fillOpacity={color ? 0.38 : undefined}
        stroke={outlineColor ?? color ?? '#196FE0'}
        strokeWidth="2"
      />
    </svg>
  );
}

function StampGlyph({ className, color }: GlyphProps) {
  return (
    <svg
      role="img"
      aria-label="Stamp"
      width="13"
      height="13"
      viewBox="0 0 15 15"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      className={className}
    >
      <path
        d="M13.4999 0.899902V13.4999L7.1999 7.1999L0.899902 13.4999V0.899902L7.1999 7.1999L13.4999 0.899902Z"
        fill={color ?? '#393b3d'}
      />
    </svg>
  );
}

function DocumentGlyph({ className }: GlyphProps) {
  return (
    <svg
      role="img"
      aria-label="Document"
      width="16"
      height="16"
      viewBox="0 0 16 16"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      className={className}
    >
      <g clipPath="url(#layer-kind-pdf-clip)">
        <path
          d="M4.49901 9.82604C4.49901 9.5724 4.32301 9.42114 4.01248 9.42114C3.88568 9.42114 3.7998 9.43366 3.75488 9.44563V10.2594C3.80807 10.2714 3.87345 10.2754 3.96357 10.2754C4.29456 10.2754 4.49901 10.1082 4.49901 9.82604Z"
          fill="#E91946"
        />
        <path
          d="M6.42124 9.42969C6.28218 9.42969 6.19231 9.44169 6.13916 9.45414V11.2564C6.19235 11.2689 6.27822 11.2689 6.35586 11.2689C6.92 11.2729 7.28792 10.9624 7.28792 10.3044C7.29192 9.7322 6.95665 9.42969 6.42124 9.42969Z"
          fill="#E91946"
        />
        <path
          d="M9.99286 0H3.85872C2.96258 0 2.23321 0.729869 2.23321 1.6255V7.63633H2.07445C1.71276 7.63633 1.41943 7.92937 1.41943 8.29138V12.2638C1.41943 12.6258 1.71273 12.9188 2.07445 12.9188H2.23321V13.6472C2.23321 14.5438 2.96258 15.2727 3.85872 15.2727H12.2285C13.1241 15.2727 13.8535 14.5438 13.8535 13.6472V3.84713L9.99286 0ZM3.13781 9.00826C3.32978 8.97582 3.5996 8.95137 3.97976 8.95137C4.36393 8.95137 4.63775 9.0247 4.82173 9.172C4.99747 9.31081 5.11607 9.53993 5.11607 9.80956C5.11607 10.0791 5.0262 10.3083 4.86269 10.4636C4.65001 10.6638 4.33549 10.7536 3.96756 10.7536C3.88567 10.7536 3.81228 10.7496 3.75488 10.7416V11.7266H3.13781V9.00826ZM12.2285 14.2772H3.85872C3.51173 14.2772 3.22917 13.9947 3.22917 13.6472V12.9188H11.0315C11.3933 12.9188 11.6866 12.6258 11.6866 12.2638V8.29138C11.6866 7.92937 11.3933 7.63633 11.0315 7.63633H3.22917V1.6255C3.22917 1.27902 3.51176 0.996462 3.85872 0.996462L9.62044 0.990443V3.12018C9.62044 3.74225 10.1252 4.24747 10.7477 4.24747L12.834 4.24148L12.8575 13.6472C12.8575 13.9947 12.5755 14.2772 12.2285 14.2772ZM5.51397 11.7142V9.00826C5.74283 8.97183 6.04113 8.95137 6.35594 8.95137C6.87912 8.95137 7.21838 9.04523 7.48421 9.24539C7.77028 9.45807 7.94999 9.79708 7.94999 10.2838C7.94999 10.811 7.75802 11.175 7.49216 11.3996C7.20209 11.6407 6.76055 11.7551 6.22112 11.7551C5.89808 11.7551 5.66921 11.7346 5.51397 11.7142ZM9.97494 10.1121V10.6188H8.98566V11.7266H8.36036V8.97183H10.0443V9.48253H8.98566V10.1121H9.97494Z"
          fill="#E91946"
        />
      </g>
      <defs>
        <clipPath id="layer-kind-pdf-clip">
          <rect width="15.2727" height="15.2727" rx="4" fill="white" />
        </clipPath>
      </defs>
    </svg>
  );
}

/** Picture-frame icon for raster/imaging layers — same family as the document glyph. */
function ImageGlyph({ className, color }: GlyphProps) {
  return (
    <svg
      role="img"
      aria-label="Image"
      width="16"
      height="16"
      viewBox="0 0 16 16"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      className={className}
    >
      <rect x="1.4" y="2.4" width="13.2" height="11.2" rx="1.6" stroke={color ?? '#E91946'} strokeWidth="1.2" />
      <circle cx="5.4" cy="6.2" r="1.3" fill={color ?? '#E91946'} />
      <path
        d="M2.6 12.4L6.4 8.6L8.8 11L11.2 8.2L13.4 10.8V12.4H2.6Z"
        fill={color ?? '#E91946'}
        stroke={color ?? '#E91946'}
        strokeWidth="0.8"
        strokeLinejoin="round"
      />
    </svg>
  );
}

const LAYER_KIND_GLYPHS: Record<LayerKind, (props: GlyphProps) => React.JSX.Element> = {
  line: LineGlyph,
  lineDashed: LineDashedGlyph,
  area: AreaGlyph,
  areaFilled: AreaFilledGlyph,
  circle: CircleGlyph,
  stamp: StampGlyph,
  document: DocumentGlyph,
  image: ImageGlyph,
};

const DEFAULT_KIND: LayerKind = 'area';

type LayerKindIconProps = {
  kind?: LayerKind;
  /** Glyph color override (the layer's tile-config paint color). */
  color?: string;
  /** Outline/stroke override for area/circle kinds. Falls back to `color`. */
  outlineColor?: string;
  size?: number;
  className?: string;
};

/**
 * Renders the layer-type glyph for a `LayerKind` inside a rounded 24×24 thumbnail frame.
 * Falls back to the `area` glyph for an unknown/omitted kind.
 */
function LayerKindIcon({ kind = DEFAULT_KIND, color, outlineColor, size = 24, className }: LayerKindIconProps) {
  const Glyph = LAYER_KIND_GLYPHS[kind] ?? LAYER_KIND_GLYPHS[DEFAULT_KIND];
  return (
    <div
      className={`border-grey-30 flex shrink-0 items-center justify-center overflow-hidden rounded-md border bg-white ${className ?? ''}`}
      style={{ width: size, height: size }}
    >
      <Glyph color={color} outlineColor={outlineColor} />
    </div>
  );
}

export { LayerKindIcon };
export type { LayerKindIconProps };
